import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import {
  Accordion,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { ptBR } from "date-fns/locale";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineExpandMore } from "react-icons/md";
import { toast } from "react-toastify";
import { UserContext } from "../../../context/UserContext";
import AttToken from "../../../helpers/attToken";
import { trackEventMatomo } from "../../../helpers/matomo";
import QueryFn from "../../../helpers/QueryFn";
import Clients from "../../../service/Clients";
import { TicketsV3 } from "../../../service/Tickets";
import queryClient from "../../../service/query";
import { ClientType } from "../../../types/clientes/types";
import { ChamadoChecklist } from "../../../types/new-form-filling/types";
import { ConjuntoType, PlaceType, UnidadeType } from "../../../types/places/types";
import { ModalEditTicketsProps, chamadosAssociacoesTypeReport, confirmationProps } from "../../../types/reports/types";

type props = {
  chamado: ChamadoChecklist
  setChamados: Function,
  chamados: ChamadoChecklist[],
  index: number,
  filterFunction: Function,
};

const queryFn = new QueryFn();
const clienteService = new Clients();
const ticketsService = new TicketsV3();

function ChamadoCard({ chamado, index, setChamados, chamados, filterFunction }: props) {
  const [instrucao, setInstrucao] = useState(chamado.textChamado || '');
  const [data, setData] = useState<Date | number | undefined>(chamado.dateChamado as number || undefined);
  const { t } = useTranslation('translation');
  const [expand, setExpand] = useState<boolean>(false);

  const { userData } = useContext(UserContext);

  return (
    <Accordion style={{ width: '100%' }} expanded={expand} key={index}>
      <AccordionSummary
        onClick={() => {
          setExpand(!expand);
          trackEventMatomo('Modal abertura de chamado logado', 'click', 'acordeon', 'abre/fecha');
        }}
        expandIcon={<MdOutlineExpandMore color="#18A689" />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Stack direction='row' justifyContent='space-between' width='100%'>
          <Stack direction='column' spacing={.2}>
            <Typography fontSize={13} component='p'>
              Chamado:
            </Typography>
            <Typography variant='subtitle1' fontSize={20} color='textDisabled' fontWeight={600} sx={{ wordBreak: 'break-word' }} component='p'>
              {chamado.selecionado ? chamado.selecionado.nome : t('Reports.modalDetailP.nameNotFound')}
            </Typography>
          </Stack>
          {chamado.adicionadoApos &&
            <IconButton
              sx={{ marginRight: 2 }}
              color="error"
              onClick={() => {
                filterFunction(index);
              }}>
              <DeleteIcon fontSize="large" />
            </IconButton>
          }
        </Stack>
      </AccordionSummary>
      <Stack
        direction="column"
        justifyContent="flex-start"
        alignItems="stretch"
        spacing={1.5}
        padding={2}
      >
        <TextField
          fullWidth
          multiline
          rows={4}
          label={t('Reports.modalDetailP.callInstructions')}
          onClick={() => trackEventMatomo('Modal de visualização de form', 'click', 'textarea', 'edita valor')}
          value={instrucao}
          onChange={(e) => {
            setInstrucao(e.target.value);
            chamados[index].textChamado = e.target.value;
            setChamados([...chamados]);
          }}
          disabled={!chamado.adicionadoApos}
        />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DesktopDatePicker
              disabled={!chamado.adicionadoApos}
              slotProps={{
                textField: {
                  required: true,
                },
                actionBar: {
                  actions: ['clear'],
                },
                field: {
                  readOnly: true,
                },
              }}
              localeText={{
                clearButtonLabel: 'Limpar',
              }}
              minDate={new Date(Date.now())}
              format='dd/MM/yyyy'
              label={t('Reports.modalDetailP.callDeadline')}
              value={data === undefined ? undefined : new Date(data)}
              onChange={(newValue) => {
                trackEventMatomo('Modal de visualização de form', 'click', 'datepicker', 'edita data');
                setData(newValue?.getTime());
                chamados[index].dateChamado = newValue?.getTime();
                setChamados([...chamados]);
              }}
            />
          </LocalizationProvider>
          {userData.migrado
            && <FormControl sx={{ width: 100 }}>
              <InputLabel id="demo-simple-select-label">{t('Reports.modalDetailP.priority')}</InputLabel>
              <Select
                disabled={!chamado.adicionadoApos}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label={t('Reports.modalDetailP.priority')}
                value={chamados[index]?.prioridade}
                onClick={() => trackEventMatomo('Modal de visualização de form', 'click', 'select', 'seleciona valor')}
                defaultValue={chamados[index]?.prioridade}
                onChange={(e) => {
                  const prioridade = e.target.value as 'alta' | 'media' | 'baixa';
                  chamados[index].prioridade = prioridade;
                  setChamados([...chamados]);
                }}
              >
                <MenuItem value="alta">{t('Reports.modalDetailP.high')}</MenuItem>
                <MenuItem value="media">{t('Reports.modalDetailP.medium')}</MenuItem>
                <MenuItem value="baixa">{t('Reports.modalDetailP.low')}</MenuItem>
              </Select>
            </FormControl>}
        </Stack>
        {data === undefined
          && <Typography
            fontWeight={600}
            color='error'
            textAlign='center'
          >
            {t('Reports.modalDetailP.openingDateRequired')}
          </Typography>}
      </Stack>
    </Accordion>
  );
}

export default function ModalEditTicketsAfter({
  chamados,
  open,
  setOpen,
  currentElement,
  chamadosConfig,
  setChamados,
  selected
}: ModalEditTicketsProps) {
  const { t } = useTranslation('translation');
  const [openModalConfirm, setOpenModalConfirm] = useState(false);

  const filterFunction = (index: number) => {
    setChamados((prev: ChamadoChecklist[]) => {
      return prev.filter((_e, i) => i !== index);
    });
  };

  return (
    <>
      {openModalConfirm
        && <ConfirmationModal
          open={openModalConfirm}
          setOpen={setOpenModalConfirm}
          source={currentElement.source}
          chamados={chamados}
          close={setOpen}
          setChamados={setChamados}
          selected={selected}
        />}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        scroll={'paper'}
      >
        <DialogTitle>Abertura de chamados</DialogTitle>
        <DialogContent dividers={true}>
          <Stack direction="column"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={2}
          >
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">{t('preenchimento.addCall')}</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label={t('preenchimento.addCall')}
                onClick={() => trackEventMatomo('Modal de visualização de form', 'click', 'select', 'seleciona valor')}
                defaultValue={""}
                onChange={(event) => {
                  const chamado = JSON.parse(event.target.value);
                  chamados.push({ selecionado: chamado, adicionadoApos: true, prioridade: 'baixa' });
                  setChamados([...chamados]);
                  event.target.value = "";
                }}
              >
                <MenuItem value="" disabled>{t('select')}</MenuItem>
                {
                  chamadosConfig.tipoChamado?.map(e => <MenuItem value={JSON.stringify(e)}>{e.nome}</MenuItem>)
                }
              </Select>
            </FormControl>
            <Stack
              sx={{ marginTop: '10px' }}
              direction="column"
              justifyContent="flex-start"
              alignItems="stretch"
              spacing={1}
            >
              {chamados?.map((e, i) => (
                <ChamadoCard
                  filterFunction={filterFunction}
                  key={i}
                  chamado={e}
                  index={i}
                  setChamados={setChamados}
                  chamados={chamados}
                />
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={chamados.some((e) => e.adicionadoApos && !e.dateChamado)}
            onClick={() => {
              trackEventMatomo('Modal de visualização de form', 'click', 'button', 'salva');
              setOpenModalConfirm(true);
            }}
          >{t('save')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ConfirmationModal({
  open,
  setOpen,
  source,
  chamados,
  close,
  setChamados,
  selected
}: confirmationProps) {
  const ocultos = selected.camposOcultos;

  const { t } = useTranslation('translation');
  const [loading, setLoading] = useState(false);
  const [chamadoAssociacoes, setChamadoAssociacoes] = useState<chamadosAssociacoesTypeReport | null>({
    cliente: ocultos ? ocultos.cliente ? ocultos.cliente === "all" ? null : ocultos.cliente || null : null : null,
    local: ocultos ? ocultos.local ? ocultos.local === "all" ? null : ocultos.local || null : null : null,
    conjunto: ocultos ? ocultos.conjunto ? ocultos.conjunto === "all" ? null : ocultos.conjunto || null : null : null,
    unidade: ocultos ? ocultos.unidade ? ocultos.unidade === "all" ? null : ocultos.unidade || null : null : null,
  });

  const { hierarchyLabel, userData } = useContext(UserContext);

  const { data: hierarquia, isLoading } = queryFn.useFetch('GET_HIERARCHY',
    async () => {
      try {
        const token = await AttToken();
        if (token) {
          const result = await clienteService.getAllHierarchy(token);
          return result;
        }
      } catch (error) {
        console.error(error);
      }
    }
  );

  const findName = (key: 'clientes' | 'conjuntos' | 'locais', id: string): string => {
    const selected = hierarquia
      ? hierarquia[key].find((el: ConjuntoType | ClientType | PlaceType) => el.id === id)
      : { nome: t('Reports.modalDetailP.nameNotFound'), id: 'A' };
    return selected
      ? selected.nome
      : t('Reports.modalDetailP.nameNotFound');
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && setOpen(false)}
      fullWidth
      maxWidth="sm"
    >
      <DialogContent dividers>
          {userData.migrado
            && <Typography
              variant="body1"
              textAlign='center'
              fontWeight='bold'>
              {t('Reports.modalDetailP.hierarchyConfirmation')}
            </Typography>}
          {userData.migrado
            ? <>
              <Typography variant="body2" textAlign='center'>{t('Reports.modalDetailP.hierarchyConfiguration')}</Typography>
              <Stack
                direction="column"
                justifyContent="flex-start"
                alignItems="stretch"
                spacing={1}
              >
                {isLoading
                  ? <>
                    <Skeleton variant="text" />
                    <Skeleton variant="rounded" height={40} />
                    <Skeleton variant="rounded" height={40} />
                    <Skeleton variant="rounded" height={40} />
                    <Skeleton variant="rounded" height={40} />

                  </>
                  : <>
                    {chamadoAssociacoes
                      && chamadoAssociacoes.cliente
                      && <Typography
                        variant="body2"
                        color='textSecondary'
                        fontWeight='bold'
                        style={{
                          wordBreak: 'break-word',
                          backgroundColor: '#FFE08A',

                        }}>
                        {
                          chamadoAssociacoes.cliente && !chamadoAssociacoes.local
                            ? `${t('Reports.modalDetailP.teamsServingAll')} ${hierarchyLabel.conjunto} ${t('Reports.modalDetailP.ofAll')} ${hierarchyLabel.local} ${t('Reports.modalDetailP.of')} ${hierarchyLabel.cliente} "${findName('clientes', chamadoAssociacoes.cliente)}" ${t('Reports.modalDetailP.willReceiveCalls')}`
                            : chamadoAssociacoes.cliente && chamadoAssociacoes.local && !chamadoAssociacoes.conjunto
                              ? `${t('Reports.modalDetailP.teamsServingAll')} ${hierarchyLabel.conjunto} ${t('Reports.modalDetailP.of')} ${hierarchyLabel.local} "${findName('locais', chamadoAssociacoes.local)}" ${t('Reports.modalDetailP.willReceiveCalls')}`
                              : chamadoAssociacoes.cliente && chamadoAssociacoes.local && chamadoAssociacoes.conjunto
                                ? `${t('Reports.modalDetailP.teamsServingSpecific')} ${hierarchyLabel.conjunto} "${findName('conjuntos', chamadoAssociacoes.conjunto)}" ${t('Reports.modalDetailP.willReceiveCalls')}`
                                : ('')
                        }
                      </Typography>}
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">{hierarchyLabel.cliente}</InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label={hierarchyLabel.cliente}
                        onClick={() => trackEventMatomo('Modal de visualização de form', 'click', 'select', 'seleciona valor')}
                        onChange={(e) => {
                          const clienteId = e.target.value;
                          setChamadoAssociacoes({ cliente: clienteId, conjunto: null, local: null, unidade: null });
                        }}
                        value={chamadoAssociacoes ? chamadoAssociacoes.cliente || "" : ""}
                      >
                        {hierarquia.clientes && hierarquia.clientes.map((cl: ClientType) => (
                          <MenuItem value={cl.id}>{cl.nome}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">{hierarchyLabel.local}</InputLabel>
                      <Select
                        onClick={() => trackEventMatomo('Modal de visualização de form', 'click', 'select', 'seleciona valor')}
                        onChange={(e) => {
                          const id = e.target.value;
                          setChamadoAssociacoes((prev) => prev && { ...prev, local: id, conjunto: null, unidade: null });
                        }}
                        value={chamadoAssociacoes ? chamadoAssociacoes.local || "" : ""}
                        disabled={!chamadoAssociacoes || !chamadoAssociacoes.cliente}
                        label={hierarchyLabel.local}
                      >
                        {chamadoAssociacoes && chamadoAssociacoes.cliente && hierarquia.locais &&
                          hierarquia.locais.filter((cl: PlaceType) => cl.cliente === chamadoAssociacoes.cliente).map((cl: PlaceType) => (
                            <MenuItem value={cl.id}>{cl.nome}</MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">{hierarchyLabel.conjunto}</InputLabel>
                      <Select
                        onClick={() => trackEventMatomo('Modal de visualização de form', 'click', 'select', 'seleciona valor')}
                        onChange={(e) => {
                          const id = e.target.value;
                          setChamadoAssociacoes((prev) => prev && { ...prev, conjunto: id, unidade: null });
                        }}
                        value={chamadoAssociacoes ? chamadoAssociacoes.conjunto || "" : ""}
                        disabled={!chamadoAssociacoes || !chamadoAssociacoes.local}
                        label={hierarchyLabel.conjunto}
                      >
                        {chamadoAssociacoes
                          && chamadoAssociacoes?.local
                          && hierarquia?.conjuntos
                          && hierarquia?.conjuntos.filter((cnj: ConjuntoType) => cnj.local === chamadoAssociacoes.local)?.map((cnj: ConjuntoType) => (
                            <MenuItem value={cnj.id}>{cnj.nome}</MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">{hierarchyLabel.unidade}</InputLabel>
                      <Select
                        onClick={() => trackEventMatomo('Modal de visualização de form', 'click', 'select', 'seleciona valor')}
                        onChange={(e) => {
                          const id = e.target.value;
                          setChamadoAssociacoes((prev) => prev && { ...prev, unidade: id });
                        }}
                        value={chamadoAssociacoes ? chamadoAssociacoes.unidade || "" : ""}
                        disabled={!chamadoAssociacoes || !chamadoAssociacoes.conjunto}
                        label={hierarchyLabel.unidade}
                      >
                        {chamadoAssociacoes
                          && chamadoAssociacoes?.conjunto
                          && hierarquia.unidades
                          && hierarquia?.unidades?.filter((uni: UnidadeType) => uni.conjunto === chamadoAssociacoes.conjunto)?.map((uni: UnidadeType) => (
                            <MenuItem value={uni.id}>{uni.nome}</MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </>
                }
              </Stack>
            </>
            : (
              <Typography fontWeight='bold' variant="body1">{t('Reports.modalDetailP.areYouSure')}</Typography>
            )}
      </DialogContent>
      <DialogActions>
          <Stack
            width='100%'
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Button
              endIcon={<CheckCircleOutlineIcon />}
              disabled={loading || isLoading}
              className="button is-success"
              onClick={async () => {
                trackEventMatomo('Modal de visualização de form', 'click', 'select', 'salva');
                setLoading(true);
                toast.warn(`${t('formDetails.savingTickets')}`);
                const token = await AttToken();
                if (token) {
                  const pendingTickets = chamados.filter((chamado) => chamado.adicionadoApos);
                  const hierarchy = {
                    cliente: chamadoAssociacoes?.cliente || 'all',
                    local: chamadoAssociacoes?.local || 'all',
                    conjunto: chamadoAssociacoes?.conjunto || 'all',
                    unidade: chamadoAssociacoes?.unidade || 'all',
                  };

                  let sentAny = false;
                  for (const chamado of pendingTickets) {
                    if (chamado.selecionado?.id == null || !chamado.dateChamado) continue;

                    const requestId = chamado.postExecutionRequestId || crypto.randomUUID();
                    if (!chamado.postExecutionRequestId) {
                      chamado.postExecutionRequestId = requestId;
                      setChamados([...chamados]);
                    }

                    try {
                      await ticketsService.postExecution(token, {
                        requestId,
                        checklistId: Number(selected.inicio),
                        ...(selected.preenchimentoDeslogado
                          ? { formAnonimoId: selected.idForm ?? selected.id ?? null }
                          : {}),
                        ...source,
                        modeloId: String(chamado.selecionado.id),
                        instrucao: chamado.textChamado || '',
                        prioridade: chamado.prioridade || 'baixa',
                        dataPrevisao: Number(chamado.dateChamado),
                        hierarquia: hierarchy,
                      });
                      delete chamado.adicionadoApos;
                      sentAny = true;
                      setChamados([...chamados]);
                    } catch {
                      // Keep this and all following tickets pending so retry uses this same requestId.
                      break;
                    }
                  }

                  if (sentAny) await queryClient.refetchQueries({ queryKey: ['GET_EXECUTION'] });

                  if (!chamados.some((chamado) => chamado.adicionadoApos)) {
                    toast.success(`${t('Reports.modalDetailP.callsTriggered')}`);
                    setOpen(false);
                    close();
                  }
                } else toast.error(`${t('Reports.modalDetailP.tokenNotFound')}`);
                setLoading(false);
              }}>
              {t('yes')}
            </Button>
            <Button
              endIcon={<DoDisturbIcon />}
              className="button is-danger"
              onClick={() => setOpen(false)}
              disabled={loading || isLoading}>
              {t('no')}
            </Button>
          </Stack>
      </DialogActions>
    </Dialog>
  );
}
